import {
  Avatar,
  Field,
  Select,
  Tag,
  TagPicker,
  TagPickerControl,
  TagPickerGroup,
  TagPickerInput,
  TagPickerList,
  TagPickerOption,
  TagPickerProps,
} from '@fluentui/react-components'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { toast } from 'react-toastify'
import { RestSchemas, createRestClient } from '@/contexts/QueryProvider'
import { useGetSearchParam } from '@/hooks'
import { Button } from '../Button'
import './Form.scss'

type MessagesSubscriptionsOrganisationWorkspace = RestSchemas['Messages.Subscriptions.OrganisationWorkspace']
type MessagesSubscriptionsOrganisationTeam = RestSchemas['Messages.Subscriptions.OrganisationTeam']
type MessagesApplicationsApplicationTeam = RestSchemas['Messages.Applications.ApplicationTeam']
type MessagesSubscriptionsNewOrganisationWorkspace = RestSchemas['Messages.Subscriptions.NewOrganisationWorkspace']
type MessagesSubscriptionsNewOrganisationWorkspaceTeam =
  RestSchemas['Messages.Subscriptions.NewOrganisationWorkspaceTeam']

export type WorkspaceForm = {
  projectId?: number
  applicationId?: number
}

type AddWorkspaceFormProps = {
  onSubmit?: () => void
  orgId?: number
  selectedWorkspace?: MessagesSubscriptionsOrganisationWorkspace
  selectedProjectTeams?: MessagesSubscriptionsOrganisationTeam[]
  selectedSupportTeams?: MessagesApplicationsApplicationTeam[]
}

type WorkspacePromiseResult = {
  // subscriptionId?: number
  workspaceId?: number
  message?: string
}

export const AddWorkspaceForm = (props: AddWorkspaceFormProps) => {
  const edit = !!props.selectedWorkspace
  const editProjectTeams = !!props.selectedProjectTeams
  const editSupportTeams = !!props.selectedSupportTeams
  const orgId = useGetSearchParam<number>('orgId') ?? props.orgId

  const [selectedProjectTeamOptions, setSelectedProjectTeamOptions] = useState<MessagesSubscriptionsOrganisationTeam[]>(
    () => (edit && props.selectedProjectTeams ? props.selectedProjectTeams : [])
  )
  const [selectedSupportTeamOptions, setSelectedSupportTeamOptions] = useState<MessagesApplicationsApplicationTeam[]>(
    () => (edit && props.selectedSupportTeams ? props.selectedSupportTeams : [])
  )

  const {
    register,
    formState: { errors },
    // setError,
    // reset,
    handleSubmit,
    watch,
  } = useForm<WorkspaceForm>({
    defaultValues: props.selectedWorkspace
      ? {
          projectId: props.selectedWorkspace.projectId,
          applicationId: props.selectedWorkspace.applicationId,
        }
      : {},
  })

  const selectedAppId = watch('applicationId')

  const {
    getProjectsApiListOrganisationProjects,
    getApplicationsApiGetApplications,
    getApplicationsApiGetApplicationTeams,
    getSubscriptionsApiGetOrganisationTeams,
    putSubscriptionsApiAddOrganisationWorkspaces,
    putWorkspacesApiAddProjectTeamToWorkspace,
    putWorkspacesApiAddSupportTeamToWorkspace,
    deleteWorkspacesApiDeleteProjectTeamFromWorkspace,
    deleteWorkspacesApiDeleteSupportTeamFromWorkspace,
  } = createRestClient()

  // Queries
  const {
    data: projects,
    isPending: isProjectsPending,
    isError: isProjectsError,
  } = getProjectsApiListOrganisationProjects.useQuery({
    query: { orgId },
  })
  const {
    data: applications,
    isPending: isApplicationsPending,
    isError: isApplicationsError,
  } = getApplicationsApiGetApplications.useQuery()
  const {
    data: supportTeams,
    isPending: isSupportTeamsPending,
    isError: isSupportTeamsError,
  } = getApplicationsApiGetApplicationTeams.useQuery(
    {
      query: { orgId: orgId!, appId: selectedAppId! },
    },
    { enabled: !!orgId && !!selectedAppId }
  )
  const {
    data: projectTeams,
    isPending: isProjectTeamsPending,
    isError: isProjectTeamsError,
  } = getSubscriptionsApiGetOrganisationTeams.useQuery(
    {
      query: { orgId: orgId! },
    },
    { enabled: !!orgId && !!selectedAppId }
  )

  // Mutations
  const { mutate: addOrganisationWorkspace } = putSubscriptionsApiAddOrganisationWorkspaces.useMutation()
  const { mutateAsync: addProjectTeamToWorkspace } = putWorkspacesApiAddProjectTeamToWorkspace.useMutation()
  const { mutateAsync: addSupportTeamToWorkspace } = putWorkspacesApiAddSupportTeamToWorkspace.useMutation()
  const { mutateAsync: deleteProjectTeamFromWorkspace } =
    deleteWorkspacesApiDeleteProjectTeamFromWorkspace.useMutation()
  const { mutateAsync: deleteSupportTeamFromWorkspace } =
    deleteWorkspacesApiDeleteSupportTeamFromWorkspace.useMutation()

  const projectOnOptionSelect: TagPickerProps['onOptionSelect'] = (_e, data) => {
    setSelectedProjectTeamOptions(projectTeams?.filter((x) => data.selectedOptions.includes(x.id.toString())) ?? [])
  }
  const tagPickerProjectTeamOptions =
    projectTeams?.filter((option) => !selectedProjectTeamOptions.includes(option)) ?? []

  const supportOnOptionSelect: TagPickerProps['onOptionSelect'] = (_e, data) => {
    setSelectedSupportTeamOptions(supportTeams?.filter((x) => data.selectedOptions.includes(x.id.toString())) ?? [])
  }
  const tagPickerSupportTeamOptions =
    supportTeams?.filter((option) => !selectedSupportTeamOptions.includes(option)) ?? []

  const onSubmit = (data: WorkspaceForm) => {
    const newWorkspace: MessagesSubscriptionsNewOrganisationWorkspace = {
      projectId: data.projectId!,
      applicationId: data.applicationId!,
    }

    addOrganisationWorkspace(
      { body: newWorkspace },
      {
        onSuccess: ({ workspaceId }, _variables) => {
          toast.success('Workspace successfully added')
          const teamsToAddPromises: Promise<WorkspacePromiseResult>[] = []

          selectedProjectTeamOptions.forEach((team) => {
            const newWorkspaceTeam: MessagesSubscriptionsNewOrganisationWorkspaceTeam = {
              subscriptionId: workspaceId,
              teamId: team.id,
            }

            const newProjectTeamRequest = addProjectTeamToWorkspace(
              {
                body: newWorkspaceTeam,
              },
              {
                onSuccess: (_data, _variables) => {
                  toast.success(`Project Team ${team.name} successfully added`)
                },
              }
            )
            teamsToAddPromises.push(newProjectTeamRequest)
          })

          selectedSupportTeamOptions.forEach((team) => {
            const newWorkspaceTeam: MessagesSubscriptionsNewOrganisationWorkspaceTeam = {
              subscriptionId: workspaceId,
              teamId: team.id,
            }

            const newSupportTeamRequest = addSupportTeamToWorkspace(
              {
                body: newWorkspaceTeam,
              },
              {
                onSuccess: (_data, _variables) => {
                  toast.success(`Support Team ${team.name} successfully added`)
                },
              }
            )
            teamsToAddPromises.push(newSupportTeamRequest)
          })

          Promise.all(teamsToAddPromises)
            .catch((err) => {
              toast.error('Error adding teams to workspace')
              console.error(err)
            })
            .finally(() => {
              props.onSubmit?.()
            })
        },
      }
    )
  }

  const onEditSubmit = () => {
    if (!props.selectedWorkspace) return
    const subscriptionId = props.selectedWorkspace.workspaceId

    const teamsToAddPromises: Promise<WorkspacePromiseResult>[] = []
    const teamsToRemovePromises: Promise<WorkspacePromiseResult>[] = []

    if (editProjectTeams) {
      const addedProjectTeams = selectedProjectTeamOptions.filter(
        (current) => !props.selectedProjectTeams?.find((original) => original.id === current.id)
      )
      const removedProjectTeams =
        props.selectedProjectTeams?.filter(
          (original) => !selectedProjectTeamOptions.find((current) => current.id === original.id)
        ) ?? []

      addedProjectTeams.forEach((team) => {
        const newProjectTeam: MessagesSubscriptionsNewOrganisationWorkspaceTeam = {
          subscriptionId: subscriptionId,
          teamId: team.id,
        }

        const newProjectTeamRequest = addProjectTeamToWorkspace(
          {
            body: newProjectTeam,
          },
          {
            onSuccess: (_data, _variables) => {
              toast.success(`Project Team ${team.name} successfully added`)
            },
          }
        )
        teamsToAddPromises.push(newProjectTeamRequest)
      })

      removedProjectTeams?.forEach((team) => {
        const removedWorkspaceTeam: MessagesSubscriptionsNewOrganisationWorkspaceTeam = {
          subscriptionId: subscriptionId,
          teamId: team.id,
        }

        const removeProjectTeamRequest = deleteProjectTeamFromWorkspace(
          {
            body: removedWorkspaceTeam,
          },
          {
            onSuccess: (_data, _variables) => {
              toast.success(`Project Team ${team.name} successfully removed`)
            },
          }
        )
        teamsToRemovePromises.push(removeProjectTeamRequest)
      })
    }

    if (editSupportTeams) {
      const addedSupportTeams = selectedSupportTeamOptions.filter(
        (current) => !props.selectedSupportTeams?.find((original) => original.id === current.id)
      )
      const removedSupportTeams =
        props.selectedSupportTeams?.filter(
          (original) => !selectedSupportTeamOptions.find((current) => current.id === original.id)
        ) ?? []

      addedSupportTeams.forEach((team) => {
        const newSupportTeam: MessagesSubscriptionsNewOrganisationWorkspaceTeam = {
          subscriptionId: subscriptionId,
          teamId: team.id,
        }

        const newSupportTeamRequest = addSupportTeamToWorkspace(
          {
            body: newSupportTeam,
          },
          {
            onSuccess: (_data, _variables) => {
              toast.success(`Support Team ${team.name} successfully added`)
            },
          }
        )
        teamsToAddPromises.push(newSupportTeamRequest)
      })

      removedSupportTeams?.forEach((team) => {
        const removedWorkspaceTeam: MessagesSubscriptionsNewOrganisationWorkspaceTeam = {
          subscriptionId: subscriptionId,
          teamId: team.id,
        }

        const removeSupportTeamRequest = deleteSupportTeamFromWorkspace(
          {
            body: removedWorkspaceTeam,
          },
          {
            onSuccess: (_data, _variables) => {
              toast.success(`Support Team ${team.name} successfully removed`)
            },
          }
        )
        teamsToRemovePromises.push(removeSupportTeamRequest)
      })
    }

    Promise.all(teamsToAddPromises.concat(teamsToRemovePromises))
      .then(() => props.onSubmit?.())
      .catch((_err) => {
        toast.error('Error adding teams to workspace')
      })
  }

  return (
    <form onSubmit={edit ? handleSubmit(onEditSubmit) : handleSubmit(onSubmit)}>
      <div className="form-wrapper">
        {!edit && (
          <>
            <Field
              required
              label="Project"
              validationState={errors.projectId ? 'error' : 'none'}
              validationMessage={errors.projectId?.message}
            >
              <Select
                // disabled={edit}
                //placeholder="Project"
                {...register('projectId', {
                  required: 'Project is required',
                  maxLength: 256,
                })}
              >
                <option key={'-'} value={undefined}></option>
                {isProjectsPending ? (
                  <option>Loading...</option>
                ) : (
                  !isProjectsError &&
                  projects?.map((p) => {
                    return (
                      <option key={p.projectId} value={p.projectId}>
                        {p.name}
                      </option>
                    )
                  })
                )}
              </Select>
            </Field>

            <Field
              required
              label="Application"
              validationState={errors.applicationId ? 'error' : 'none'}
              validationMessage={errors.applicationId?.message}
            >
              <Select
                // disabled={edit}
                //placeholder="Application"
                {...register('applicationId', {
                  required: 'Application is required',
                  maxLength: 256,
                })}
              >
                <option key={'-'} value={undefined}></option>
                {isApplicationsPending ? (
                  <option>Loading...</option>
                ) : (
                  !isApplicationsError &&
                  applications?.map((a) => {
                    return (
                      <option key={a.applicationId} value={a.applicationId}>
                        {a.appName}
                      </option>
                    )
                  })
                )}
              </Select>
            </Field>
          </>
        )}

        {(!edit || editProjectTeams) && (
          <Field label="Project team/s">
            <TagPicker
              onOptionSelect={projectOnOptionSelect}
              selectedOptions={selectedProjectTeamOptions.map((x) => x.id.toString())}
              // disabled={(selectedAppId as any) == ''}
              disabled={isProjectTeamsPending || isProjectTeamsError}
            >
              <TagPickerControl>
                <TagPickerGroup>
                  {selectedProjectTeamOptions.map((option) => (
                    <Tag
                      key={option.id}
                      shape="rounded"
                      media={<Avatar aria-hidden name={option.name} color="colorful" />}
                      value={option.id.toString()}
                    >
                      {option.name}
                    </Tag>
                  ))}
                </TagPickerGroup>
                <TagPickerInput aria-label="Select Teams" />
              </TagPickerControl>
              <TagPickerList>
                {tagPickerProjectTeamOptions.length > 0
                  ? tagPickerProjectTeamOptions.map((option) => (
                      <TagPickerOption
                        secondaryContent="Project Team"
                        media={<Avatar shape="square" aria-hidden name={option.name} color="colorful" />}
                        value={option.id.toString()}
                        key={option.id}
                      >
                        {option.name}
                      </TagPickerOption>
                    ))
                  : 'No project teams available'}
              </TagPickerList>
            </TagPicker>
          </Field>
        )}

        {(!edit || editSupportTeams) && (
          <Field label="Support team/s">
            <TagPicker
              onOptionSelect={supportOnOptionSelect}
              selectedOptions={selectedSupportTeamOptions.map((x) => x.id.toString())}
              // disabled={(selectedAppId as any) == ''}
              disabled={isSupportTeamsPending || isSupportTeamsError}
            >
              <TagPickerControl>
                <TagPickerGroup>
                  {selectedSupportTeamOptions.map((option) => (
                    <Tag
                      key={option.id}
                      shape="rounded"
                      media={<Avatar aria-hidden name={option.name} color="colorful" />}
                      value={option.id.toString()}
                    >
                      {option.name}
                    </Tag>
                  ))}
                </TagPickerGroup>
                <TagPickerInput aria-label="Select Teams" />
              </TagPickerControl>
              <TagPickerList>
                {tagPickerSupportTeamOptions.length > 0
                  ? tagPickerSupportTeamOptions.map((option) => (
                      <TagPickerOption
                        secondaryContent="Support Team"
                        media={<Avatar shape="square" aria-hidden name={option.name} color="colorful" />}
                        value={option.id.toString()}
                        key={option.id}
                      >
                        {option.name}
                      </TagPickerOption>
                    ))
                  : 'No support teams available'}
              </TagPickerList>
            </TagPicker>
          </Field>
        )}
      </div>
      <div className="form-action-container">
        <Button type="submit" text={edit ? 'Save' : 'Submit'} />
      </div>
    </form>
  )
}
